import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useStaffIdentity } from '../hooks/useStaffIdentity';

interface FirebaseContextType {
  user: User | null;
  tenantId: string | null;
  role: string | null;
  userName: string | null;
  staffId: string | null;
  loading: boolean;
  error: string | null;
}

const FirebaseContext = createContext<FirebaseContextType>({ user: null, tenantId: null, role: null, userName: null, staffId: null, loading: true, error: null });

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { staffId, loading: identityLoading } = useStaffIdentity();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (!user) {
        setTenantId(null);
        setRole(null);
        setUserName(null);
        setLoading(false);
        setError(null);
      }
    });
    return unsubscribeAuth;
  }, []);

  useEffect(() => {
    if (!user) return;

    setLoading(true);
    // Start listening to the user document
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        setTenantId(userData.tenantId);
        setRole(userData.role);
        setUserName(userData.name || null);
      } else {
        setTenantId(null);
        setRole(null);
        setUserName(null);
      }
      setLoading(false);
      setError(null);
    }, (err: any) => {
      console.error("Error watching user document:", err);
      setLoading(false);
      if (err.code !== 'permission-denied') {
        setError('Firestore connection issue. Please check your connection.');
      }
    });

    return () => unsubscribeUser();
  }, [user, retryCount]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(prev => prev + 1);
  };

  const finalLoading = loading || identityLoading;

  return (
    <FirebaseContext.Provider value={{ user, tenantId, role, userName, staffId, loading: finalLoading, error }}>
      {!finalLoading && children}
      {finalLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-500 font-medium">Connecting to TipValue AI...</p>
          </div>
        </div>
      )}
      {error && !finalLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-red-50 z-50 p-6">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Connection Issues</h2>
            <p className="text-gray-600 leading-relaxed">It seems there's a problem connecting to our database. You can try to continue using the application, or refresh to try reconnecting.</p>
            <button 
              onClick={handleRetry}
              className="w-full bg-gray-900 text-white rounded-xl py-3.5 font-bold hover:bg-gray-800 transition-all active:scale-95 shadow-lg shadow-gray-900/10"
            >
              Retry Connection
            </button>
          </div>
        </div>
      )}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = () => useContext(FirebaseContext);
